// =====================================================================
// Les récits d'une personne
// =====================================================================
// Un vieil homme récite la lignée ; son petit-fils tient le téléphone.
// Tout se passe donc sur la fiche de l'ancien, pas sur celle du
// petit-fils.
//
// AU VILLAGE, IL N'Y A PAS DE RÉSEAU. L'enregistrement va d'abord dans la
// file d'attente et part plus tard. On le dit clairement plutôt que de
// laisser croire que c'est envoyé.
// =====================================================================

import { useEffect, useRef, useState } from 'react';
import { api, ErreurApi } from '../api';
import type { Recit } from '../../partage/types';
import { listerRecits, retirer, RECITS, surChangement } from '../file-attente';
import { Enregistreur, microDisponible } from '../micro';
import { Alerte, IconeMicro } from './base';

/** 1 min 05 plutôt que 65 s : on lit une durée, on ne la calcule pas. */
function duree(s: number): string {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m} min ${String(r).padStart(2, '0')}` : `${r} s`;
}

export function Recits({
  personneId,
  personneNom,
}: {
  personneId: string;
  personneNom: string;
}) {
  const [liste, setListe] = useState<Recit[] | null>(null);
  const [attente, setAttente] = useState<{ cle: string; duree_s: number; erreur: string | null }[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);
  const [secondes, setSecondes] = useState(0);
  const [envoi, setEnvoi] = useState(false);
  const [titre, setTitre] = useState('');

  const magneto = useRef<Enregistreur | null>(null);
  const horloge = useRef<number | null>(null);

  async function charger() {
    try {
      setListe(await api.recits(personneId));
    } catch {
      // Hors ligne : on n'a pas la liste du serveur, mais on a la file.
      setListe([]);
    }
  }

  async function chargerAttente() {
    const tous = await listerRecits().catch(() => []);
    setAttente(
      tous
        .filter((r) => r.personne_id === personneId)
        .map((r) => ({ cle: r.cle, duree_s: r.duree_s, erreur: r.erreur })),
    );
  }

  useEffect(() => {
    void charger();
    void chargerAttente();
    return surChangement(() => void chargerAttente());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personneId]);

  // Le micro doit être rendu même si l'utilisateur ferme la fiche en
  // plein enregistrement : sans cela, le voyant du téléphone reste
  // allumé et il croit — à juste titre — qu'on l'écoute encore.
  useEffect(() => {
    return () => {
      magneto.current?.annuler();
      if (horloge.current) window.clearInterval(horloge.current);
    };
  }, []);

  async function demarrer() {
    setErreur(null);
    setInfo(null);
    try {
      const e = new Enregistreur();
      await e.demarrer();
      magneto.current = e;
      setSecondes(0);
      setEnregistre(true);
      horloge.current = window.setInterval(
        () => setSecondes((s) => s + 1),
        1000,
      );
    } catch {
      setErreur(
        "Le micro n'est pas accessible. Vérifiez que vous l'avez autorisé " +
          'pour Yambi dans les réglages de votre téléphone.',
      );
    }
  }

  async function arreter() {
    if (horloge.current) window.clearInterval(horloge.current);
    horloge.current = null;
    setEnregistre(false);

    const e = magneto.current;
    magneto.current = null;
    if (!e) return;

    setEnvoi(true);
    try {
      const prise = await e.arreter();
      const r = await api.ajouterRecit(
        personneId,
        personneNom,
        prise.son,
        prise.duree_s,
        titre.trim() || null,
      );
      setTitre('');
      setInfo(
        r.en_attente
          ? "Enregistré sur votre téléphone. Il partira dès que vous aurez du réseau."
          : 'Récit enregistré.',
      );
      await Promise.all([charger(), chargerAttente()]);
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Envoi impossible.');
    } finally {
      setEnvoi(false);
    }
  }

  function abandonner() {
    if (horloge.current) window.clearInterval(horloge.current);
    horloge.current = null;
    magneto.current?.annuler();
    magneto.current = null;
    setEnregistre(false);
  }

  async function supprimer(id: string) {
    setErreur(null);
    try {
      await api.supprimerRecit(id);
      await charger();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Suppression impossible.');
    }
  }

  const rien = (liste?.length ?? 0) === 0 && attente.length === 0;

  return (
    <div className="recits">
      <h4>
        <IconeMicro /> Sa voix
      </h4>

      {erreur && <Alerte>{erreur}</Alerte>}
      {info && <Alerte type="succes">{info}</Alerte>}

      {rien && !enregistre && (
        <p className="doux">
          Rien d'enregistré. Un ancien qui récite la lignée dit souvent ce
          qu'aucun arbre ne retient : les villages quittés, les noms
          d'avant, pourquoi une branche est partie.
        </p>
      )}

      {(liste ?? []).map((r) => (
        <div className="recit" key={r.id}>
          <audio controls preload="none" src={api.urlRecit(r.id)}>
            Votre navigateur ne sait pas lire ce son.
          </audio>
          <div className="recit-pied">
            <span className="doux">
              {r.titre ? `${r.titre} · ` : ''}
              {duree(r.duree_s)}
              {r.depose_par ? ` · enregistré par ${r.depose_par}` : ''}
            </span>
            {r.a_moi && (
              <button
                className="bouton danger mince"
                onClick={() => supprimer(r.id)}
              >
                Supprimer
              </button>
            )}
          </div>
        </div>
      ))}

      {attente.map((r) => (
        <div className="recit en-attente" key={r.cle}>
          <div className="recit-pied">
            <span className="doux">
              {duree(r.duree_s)} ·{' '}
              {r.erreur ? `refusé : ${r.erreur}` : "sur votre téléphone, en attente d'envoi"}
            </span>
            {r.erreur && (
              <button
                className="bouton danger mince"
                onClick={() => void retirer(r.cle, RECITS)}
              >
                Abandonner
              </button>
            )}
          </div>
        </div>
      ))}

      {!microDisponible() ? (
        <p className="doux">
          Ce navigateur ne sait pas enregistrer de son. Ouvrez Yambi depuis
          l'application installée, ou depuis Chrome.
        </p>
      ) : enregistre ? (
        <div className="carte enregistrement">
          <div className="pastille-micro" aria-hidden="true" />
          <div className="chrono">{duree(secondes)}</div>
          <p className="doux">
            Laissez-le parler. Vous couperez à la fin.
          </p>
          <div className="rangee-boutons">
            <button className="bouton secondaire" onClick={abandonner}>
              Abandonner
            </button>
            <button className="bouton" onClick={arreter}>
              Terminer
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="champ">
            <label htmlFor="titre-recit">De quoi parle-t-il ? (facultatif)</label>
            <input
              id="titre-recit"
              value={titre}
              maxLength={200}
              onChange={(e) => setTitre(e.target.value)}
              placeholder="La lignée depuis Kananga"
              autoComplete="off"
            />
          </div>
          <button className="bouton" onClick={demarrer} disabled={envoi}>
            {envoi ? 'Un instant…' : 'Enregistrer sa voix'}
          </button>
        </>
      )}
    </div>
  );
}
